/**
 * SmartLink auto-rank — operator page.
 *
 * Three sections stacked inside a single card-heavy shell:
 *   1. Stats editor — PUT /stats for one offer at a time.
 *   2. Stats lookup — GET /stats/:offer_id table view.
 *   3. Weight sliders + rank preview — POST /rank on demand.
 *
 * Data layer is TanStack Query + Sonner for feedback, matching the OS
 * targeting form. Form state is hand-rolled; zod/react-hook-form would pull
 * extra dependency surface outside the agent lane.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	autorankQueryKeys,
	clampWeightPercent,
	DEFAULT_WEIGHT_PERCENTS,
	getOfferStats,
	normalizeWeights,
	parseOfferIdList,
	putOfferStats,
	rankOffers,
	statsFormSchema,
	sumWeights,
	WEIGHT_HINTS,
	WEIGHT_KEYS,
	WEIGHT_LABELS,
	type OfferStatsInput,
	type OfferStatsRow,
	type RankResponse,
	type WeightKey,
	type WeightPercents,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — grouped so the JSX stays readable. Light theme, emerald→sky
// gradient reserved for the headline, zinc borders, 6–8px rounding.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const PRIMARY_BTN =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const GHOST_BTN =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Shared types — used across the three sub-panels.
// ---------------------------------------------------------------------------

interface StatsFormState {
	offer_id: string;
	epc_cents: string;
	cr_bps: string;
	payout_cents: string;
	history_clicks: string;
}

const EMPTY_STATS_FORM: StatsFormState = {
	offer_id: "",
	epc_cents: "",
	cr_bps: "",
	payout_cents: "",
	history_clicks: "",
};

export interface SmartlinkAutorankPageProps {
	/** When present, prefills the stats lookup with this offer id. */
	initialOfferId?: string;
}

export function SmartlinkAutorankPage(props: SmartlinkAutorankPageProps = {}) {
	return (
		<main className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Heading />
			<StatsEditor />
			<StatsLookup initialOfferId={props.initialOfferId} />
			<RankPreview />
			<DocHint />
		</main>
	);
}

export default SmartlinkAutorankPage;

// ---------------------------------------------------------------------------
// Header — gradient headline + copy. No emojis, no competitor names.
// ---------------------------------------------------------------------------

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				SmartLink
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Auto-rank by EPC, CR, payout, history
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Feed rolling performance stats into each offer, tune the weighting,
				and preview the composite ranking the smart-link router will use at
				the edge. Scores are computed via z-score blending across the batch.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Stats editor — PUT /stats form.
// ---------------------------------------------------------------------------

function StatsEditor() {
	const queryClient = useQueryClient();
	const [form, setForm] = useState<StatsFormState>(EMPTY_STATS_FORM);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const mutation = useMutation({
		mutationFn: (payload: OfferStatsInput) => putOfferStats(payload),
		onSuccess: (row) => {
			toast.success(`Stats saved for ${row.offer_id}.`);
			queryClient.setQueryData(autorankQueryKeys.stats(row.offer_id), row);
			setForm(EMPTY_STATS_FORM);
			setErrors({});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to save offer stats.");
		},
	});

	function update<K extends keyof StatsFormState>(key: K, value: string) {
		setForm((prev) => ({ ...prev, [key]: value }));
		if (errors[key]) {
			setErrors((prev) => {
				const { [key]: _drop, ...rest } = prev;
				return rest;
			});
		}
	}

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = statsFormSchema.safeParse(form);
		if (!parsed.success) {
			setErrors(parsed.errors);
			toast.error("Please fix the highlighted fields.");
			return;
		}
		setErrors({});
		mutation.mutate(parsed.data);
	}

	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`}>
			<SectionHeading
				eyebrow="Editor"
				title="Stats editor"
				description="Upsert one offer's rolling numbers. All values are non-negative integers."
			/>
			<form
				aria-label="Stats editor"
				onSubmit={handleSubmit}
				className="grid grid-cols-1 gap-4 sm:grid-cols-2"
				noValidate
			>
				<StatsField
					label="Offer ID"
					name="offer_id"
					placeholder="e.g. off_123"
					value={form.offer_id}
					onChange={(v) => update("offer_id", v)}
					error={errors.offer_id}
					className="sm:col-span-2"
				/>
				<StatsField
					label="EPC (cents)"
					name="epc_cents"
					placeholder="0"
					value={form.epc_cents}
					onChange={(v) => update("epc_cents", v)}
					error={errors.epc_cents}
					inputMode="numeric"
					hint="Earnings per click over the current window."
				/>
				<StatsField
					label="CR (bps)"
					name="cr_bps"
					placeholder="0"
					value={form.cr_bps}
					onChange={(v) => update("cr_bps", v)}
					error={errors.cr_bps}
					inputMode="numeric"
					hint="Conversion rate in basis points (100 = 1%)."
				/>
				<StatsField
					label="Payout (cents)"
					name="payout_cents"
					placeholder="0"
					value={form.payout_cents}
					onChange={(v) => update("payout_cents", v)}
					error={errors.payout_cents}
					inputMode="numeric"
					hint="Gross payout per conversion."
				/>
				<StatsField
					label="History clicks"
					name="history_clicks"
					placeholder="0"
					value={form.history_clicks}
					onChange={(v) => update("history_clicks", v)}
					error={errors.history_clicks}
					inputMode="numeric"
					hint="Total click volume (log-scaled for ranking)."
				/>
				<div className="sm:col-span-2 flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
					<button
						type="button"
						onClick={() => {
							setForm(EMPTY_STATS_FORM);
							setErrors({});
						}}
						className={GHOST_BTN}
						disabled={mutation.isPending}
					>
						Reset
					</button>
					<button
						type="submit"
						className={PRIMARY_BTN}
						disabled={mutation.isPending}
					>
						{mutation.isPending ? "Saving." : "Save stats"}
					</button>
				</div>
			</form>
		</section>
	);
}

interface StatsFieldProps {
	label: string;
	name: string;
	value: string;
	onChange: (v: string) => void;
	placeholder?: string;
	error?: string;
	hint?: string;
	inputMode?: "numeric" | "text";
	className?: string;
}

function StatsField(props: StatsFieldProps) {
	const id = `stats-${props.name}`;
	return (
		<div className={`flex flex-col gap-1.5 ${props.className ?? ""}`}>
			<label htmlFor={id} className={LABEL}>
				{props.label}
			</label>
			<input
				id={id}
				name={props.name}
				type="text"
				inputMode={props.inputMode ?? "text"}
				autoComplete="off"
				placeholder={props.placeholder}
				className={INPUT}
				value={props.value}
				onChange={(e) => props.onChange(e.currentTarget.value)}
				aria-invalid={Boolean(props.error)}
				aria-describedby={props.error ? `${id}-error` : undefined}
			/>
			{props.error ? (
				<p id={`${id}-error`} className={ERROR_TEXT}>
					{props.error}
				</p>
			) : props.hint ? (
				<p className={HINT}>{props.hint}</p>
			) : null}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Stats lookup — GET /stats/:offer_id rendered as a tiny table.
// ---------------------------------------------------------------------------

function StatsLookup(props: { initialOfferId?: string }) {
	const [offerId, setOfferId] = useState(props.initialOfferId ?? "");
	const trimmed = offerId.trim();
	const enabled = trimmed.length > 0;

	const query = useQuery({
		queryKey: autorankQueryKeys.stats(trimmed),
		queryFn: () => getOfferStats(trimmed),
		enabled,
	});

	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`}>
			<SectionHeading
				eyebrow="Lookup"
				title="Offer stats table"
				description="Inspect the persisted row for any offer. Missing rows render as zero."
			/>
			<div className="flex flex-col gap-2 sm:flex-row sm:items-end">
				<div className="flex flex-1 flex-col gap-1.5">
					<label htmlFor="stats-lookup-id" className={LABEL}>
						Offer ID
					</label>
					<input
						id="stats-lookup-id"
						name="stats-lookup-id"
						type="text"
						autoComplete="off"
						placeholder="e.g. off_123"
						className={INPUT}
						value={offerId}
						onChange={(e) => setOfferId(e.currentTarget.value)}
					/>
				</div>
				<button
					type="button"
					className={GHOST_BTN}
					onClick={() => query.refetch()}
					disabled={!enabled || query.isFetching}
				>
					{query.isFetching ? "Loading." : "Refresh"}
				</button>
			</div>
			<StatsTable data={query.data} loading={query.isFetching} error={query.error} />
		</section>
	);
}

function StatsTable(props: {
	data?: OfferStatsRow;
	loading: boolean;
	error: unknown;
}) {
	if (props.error instanceof Error) {
		return (
			<p className={ERROR_TEXT}>
				{props.error.message || "Unable to load stats."}
			</p>
		);
	}
	if (!props.data) {
		return (
			<p className={HINT}>
				{props.loading ? "Loading stats." : "Enter an offer id to load stats."}
			</p>
		);
	}
	const rows: Array<{ key: string; label: string; value: string }> = [
		{ key: "offer_id", label: "Offer ID", value: props.data.offer_id },
		{
			key: "epc_cents",
			label: "EPC (cents)",
			value: props.data.epc_cents.toString(),
		},
		{ key: "cr_bps", label: "CR (bps)", value: props.data.cr_bps.toString() },
		{
			key: "payout_cents",
			label: "Payout (cents)",
			value: props.data.payout_cents.toString(),
		},
		{
			key: "history_clicks",
			label: "History clicks",
			value: props.data.history_clicks.toString(),
		},
		{
			key: "updated_at",
			label: "Updated (ms)",
			value: props.data.updated_at.toString(),
		},
	];
	return (
		<div className="overflow-hidden rounded-[6px] border border-zinc-200">
			<table className="w-full text-left text-sm">
				<thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
					<tr>
						<th scope="col" className="px-3 py-2 font-medium">
							Field
						</th>
						<th scope="col" className="px-3 py-2 font-medium">
							Value
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr key={row.key} className="border-t border-zinc-100">
							<th
								scope="row"
								className="whitespace-nowrap px-3 py-2 font-medium text-zinc-700"
							>
								{row.label}
							</th>
							<td className="px-3 py-2 font-mono text-zinc-900">{row.value}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Rank preview — weight sliders + textarea + POST /rank trigger.
// ---------------------------------------------------------------------------

function RankPreview() {
	const [weights, setWeights] = useState<WeightPercents>({
		...DEFAULT_WEIGHT_PERCENTS,
	});
	const [idsInput, setIdsInput] = useState("");
	const [result, setResult] = useState<RankResponse | null>(null);

	const offerIds = useMemo(() => parseOfferIdList(idsInput), [idsInput]);
	const rawSum = sumWeights(weights);
	const normalized = useMemo(() => normalizeWeights(weights), [weights]);

	const mutation = useMutation({
		mutationFn: () =>
			rankOffers({ offer_ids: offerIds, weights: normalized }),
		onSuccess: (res) => {
			setResult(res);
			toast.success(`Ranked ${res.offers.length} offers.`);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to rank offers.");
		},
	});

	function updateWeight(key: WeightKey, value: number) {
		setWeights((prev) => ({ ...prev, [key]: clampWeightPercent(value) }));
	}

	function handleRank() {
		if (offerIds.length === 0) {
			toast.error("Add at least one offer id.");
			return;
		}
		mutation.mutate();
	}

	return (
		<section className={`${CARD} flex flex-col gap-5 p-6`}>
			<SectionHeading
				eyebrow="Preview"
				title="Rank preview"
				description="Tune the weights, paste offer ids, and render the composite ranking."
			/>
			<div className="flex flex-col gap-4">
				<div className="flex items-center justify-between">
					<h3 className={LABEL}>Weights</h3>
					<WeightSumBadge raw={rawSum} />
				</div>
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					{WEIGHT_KEYS.map((key) => (
						<WeightSlider
							key={key}
							label={WEIGHT_LABELS[key]}
							hint={WEIGHT_HINTS[key]}
							value={weights[key]}
							normalizedPct={normalized[key] * 100}
							onChange={(v) => updateWeight(key, v)}
						/>
					))}
				</div>
			</div>
			<div className="flex flex-col gap-1.5">
				<label htmlFor="rank-ids" className={LABEL}>
					Offer IDs
				</label>
				<textarea
					id="rank-ids"
					name="rank-ids"
					rows={4}
					placeholder={"one id per line\nor, comma-delimited"}
					className={`${INPUT} font-mono`}
					value={idsInput}
					onChange={(e) => setIdsInput(e.currentTarget.value)}
				/>
				<p className={HINT}>
					{offerIds.length === 0
						? "No offers parsed yet."
						: `${offerIds.length} offer${offerIds.length === 1 ? "" : "s"} parsed.`}
				</p>
			</div>
			<div className="flex items-center justify-end">
				<button
					type="button"
					className={PRIMARY_BTN}
					onClick={handleRank}
					disabled={mutation.isPending || offerIds.length === 0}
				>
					{mutation.isPending ? "Ranking." : "Run rank"}
				</button>
			</div>
			{result && <RankTable result={result} />}
		</section>
	);
}

function WeightSumBadge(props: { raw: number }) {
	const normalized = props.raw <= 0 ? "equal split" : "auto-normalized to 1.00";
	return (
		<div
			className="inline-flex items-center gap-2 rounded-[6px] border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-xs text-zinc-600"
			data-testid="weight-sum-badge"
		>
			<span className="font-medium text-zinc-800" data-testid="weight-sum-raw">
				Sum {props.raw}
			</span>
			<span aria-hidden="true" className="h-1 w-1 rounded-full bg-zinc-300" />
			<span>{normalized}</span>
		</div>
	);
}

function WeightSlider(props: {
	label: string;
	hint: string;
	value: number;
	normalizedPct: number;
	onChange: (value: number) => void;
}) {
	const id = `weight-${props.label.toLowerCase()}`;
	return (
		<div className="flex flex-col gap-1.5">
			<div className="flex items-center justify-between">
				<label htmlFor={id} className={LABEL}>
					{props.label}
				</label>
				<div className="flex items-center gap-2 text-xs text-zinc-500">
					<span
						className="font-mono text-zinc-800"
						data-testid={`weight-${props.label.toLowerCase()}-raw`}
					>
						{props.value}
					</span>
					<span aria-hidden="true">/</span>
					<span
						className="font-mono text-zinc-500"
						data-testid={`weight-${props.label.toLowerCase()}-norm`}
					>
						{props.normalizedPct.toFixed(1)}%
					</span>
				</div>
			</div>
			<input
				id={id}
				name={id}
				type="range"
				min={0}
				max={100}
				step={1}
				value={props.value}
				onChange={(e) => props.onChange(Number(e.currentTarget.value))}
				className="h-2 w-full cursor-pointer appearance-none rounded-[6px] bg-gradient-to-r from-emerald-500 to-sky-500"
			/>
			<p className={HINT}>{props.hint}</p>
		</div>
	);
}

function RankTable(props: { result: RankResponse }) {
	if (props.result.offers.length === 0) {
		return <p className={HINT}>Rank returned zero offers.</p>;
	}
	return (
		<div className="overflow-hidden rounded-[6px] border border-zinc-200">
			<table className="w-full text-left text-sm">
				<thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
					<tr>
						<th scope="col" className="px-3 py-2 font-medium">
							Rank
						</th>
						<th scope="col" className="px-3 py-2 font-medium">
							Offer
						</th>
						<th scope="col" className="px-3 py-2 font-medium">
							Score
						</th>
						<th scope="col" className="px-3 py-2 font-medium">
							EPC
						</th>
						<th scope="col" className="px-3 py-2 font-medium">
							CR
						</th>
						<th scope="col" className="px-3 py-2 font-medium">
							Payout
						</th>
						<th scope="col" className="px-3 py-2 font-medium">
							History
						</th>
					</tr>
				</thead>
				<tbody>
					{props.result.offers.map((offer, index) => (
						<tr
							key={offer.offer_id}
							className="border-t border-zinc-100 align-top"
						>
							<td className="px-3 py-2 font-mono text-zinc-500">
								{index + 1}
							</td>
							<td className="px-3 py-2 font-mono text-zinc-900">
								{offer.offer_id}
							</td>
							<td className="px-3 py-2 font-mono text-zinc-900">
								{offer.score.toFixed(4)}
							</td>
							<td className="px-3 py-2 font-mono text-zinc-600">
								{offer.epc_cents}
							</td>
							<td className="px-3 py-2 font-mono text-zinc-600">
								{offer.cr_bps}
							</td>
							<td className="px-3 py-2 font-mono text-zinc-600">
								{offer.payout_cents}
							</td>
							<td className="px-3 py-2 font-mono text-zinc-600">
								{offer.history_clicks}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Shared fragments.
// ---------------------------------------------------------------------------

function SectionHeading(props: {
	eyebrow: string;
	title: string;
	description: string;
}) {
	return (
		<header className="flex flex-col gap-1">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				{props.eyebrow}
			</p>
			<h2 className="text-lg font-semibold text-zinc-900">{props.title}</h2>
			<p className={HINT}>{props.description}</p>
		</header>
	);
}

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How it works</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>Stats upserts land in a rolling per-tenant table.</li>
				<li>Weights are normalized client-side before the rank call.</li>
				<li>
					Ranking blends z-scores of EPC, CR, and payout across the batch,
					plus log1p(history_clicks) for volume-weighted stabilization.
				</li>
			</ul>
		</aside>
	);
}
